import { Component, ErrorInfo, ReactNode } from 'react';
import { XCircle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/Button';

interface ErrorBoundaryProps {
  /**
   * Children to render
   */
  children: ReactNode;

  /**
   * Custom fallback UI to render when an error occurs
   */
  fallback?: ReactNode;

  /**
   * Callback when an error is caught
   */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;

  /**
   * Keys that trigger error boundary reset when changed
   */
  resetKeys?: Array<string | number>;

  /**
   * Whether to show a compact version (for use within pages)
   */
  compact?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Call optional error handler (could be used for error reporting service)
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    const { resetKeys = [] } = this.props;
    const { hasError } = this.state;

    // Reset error boundary when resetKeys change
    if (hasError && resetKeys.length > 0) {
      const prevResetKeys = prevProps.resetKeys || [];
      const hasKeyChanged = resetKeys.some((key, index) => key !== prevResetKeys[index]);
      if (hasKeyChanged) {
        this.reset();
      }
    }
  }

  reset = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback, compact = false } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      const isDevelopment = import.meta.env.DEV;

      // Compact version for use within pages
      if (compact) {
        return (
          <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-6 text-center">
            <XCircle className="mx-auto mb-3 h-10 w-10 text-red-500 dark:text-red-400" />
            <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-slate-100">
              Something went wrong
            </h3>
            <p className="mb-4 text-sm text-gray-600 dark:text-slate-400">
              This section could not be loaded. Please try again.
            </p>
            <Button variant="outline" size="sm" onClick={this.reset}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
          </div>
        );
      }

      // Full page error UI
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-slate-900 p-4">
          <div className="w-full max-w-md rounded-lg bg-white dark:bg-slate-800 p-8 text-center shadow-lg dark:shadow-slate-900/50">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <XCircle className="h-10 w-10 text-red-500 dark:text-red-400" />
            </div>

            <h1 className="mb-2 text-2xl font-bold text-gray-900 dark:text-slate-100">
              Something went wrong
            </h1>

            <p className="mb-6 text-gray-600 dark:text-slate-400">
              We are sorry, but something unexpected happened. Please try refreshing the page.
            </p>

            {isDevelopment && error && (
              <details className="mb-6 text-left">
                <summary className="mb-2 cursor-pointer text-sm font-medium text-red-600 dark:text-red-400">
                  Error Details (Development Only)
                </summary>
                <pre className="max-h-48 overflow-auto rounded bg-gray-100 dark:bg-slate-900 p-3 text-xs text-gray-800 dark:text-slate-300">
                  {error.toString()}
                  {error.stack && (
                    <>
                      {'\n\n'}
                      {error.stack}
                    </>
                  )}
                </pre>
              </details>
            )}

            <div className="flex justify-center gap-3">
              <Button variant="primary" onClick={this.reset}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>

              <Button variant="outline" onClick={this.handleGoHome}>
                <Home className="mr-2 h-4 w-4" />
                Go Home
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
